import { useSessionStorage } from "@/hooks/useSessionStorage";
import { Model } from "@/types/a2i-media.types";
import {
  findModelByCatalogueId,
  findModelByProviderId,
} from "@/lib/model-catalogue";
import { create } from "zustand";

type Store = {
  models: Model[];
  setModels: (models: Model[]) => void;

  isModelsFetched: boolean;
  setIsModelsFetched: (isFetched: boolean) => void;

  selectedImageGenerationModel: Model | null;
  setSelectedImageGenerationModel: (model: Model) => void;
  setSelectedImageGenerationModelByModelId: (modelId: string) => void;

  selectedVideoGenearationModel: Model | null;
  setSelectedVideoGenearationModel: (model: Model) => void;
  setSelectedVideoGenearationModelByModelId: (modelId: string) => void;

  selectedRemixModel: Model | null;
  setSelectedRemixModel: (model: Model) => void;
  setSelectedRemixModelByModelId: (modelId: string) => void;

  selectedVtonModel: Model | null;
  setSelectedVtonModel: (model: Model) => void;

  selectedUpscaleModel: Model | null;
  setSelectedUpscaleModel: (model: Model) => void;
};

export const useModelsStore = create<Store>()((set, get) => {
  const { getSessionItem, setSessionItem, removeSessionItem } =
    useSessionStorage();

  return {
    models: [],
    setModels: (models) => {
      const selectedModelId = getSessionItem<string>(
        "a2i-image-generation-model-id",
      );
      const selectedVideoGenearationModelId = getSessionItem<string>(
        "a2i-video-generation-model-id"
      );
      const selectedRemixModelId = getSessionItem<string>(
        "a2i-remix-model-id",
      );
      const selectedVtonModelId = getSessionItem<string>(
        "a2i-vton-model-id",
      );
      const selectedUpscaleModelId = getSessionItem<string>(
        "a2i-upscale-model-id",
      );

      const imageModels = models.filter((model) => model.type === "image");
      const selectedImageGenerationModel =
        imageModels.length > 0
          ? findModelByCatalogueId(imageModels, selectedModelId) ||
            imageModels[0]
          : null;

      const videoModels = models.filter((model) => model.type === "video");
      const selectedVideoGenearationModel =
        videoModels.length > 0
          ? findModelByCatalogueId(
              videoModels,
              selectedVideoGenearationModelId,
            ) || videoModels[0]
          : null;

      const remixModels = models.filter((model) => model.type === "remix");
      const selectedRemixModel =
        remixModels.length > 0
          ? findModelByCatalogueId(remixModels, selectedRemixModelId) ||
            remixModels[0]
          : null;

      const vtonModels = models.filter((model) => model.type === "vton");
      const selectedVtonModel =
        vtonModels.length > 0
          ? findModelByCatalogueId(vtonModels, selectedVtonModelId) ||
            vtonModels[0]
          : null;

      const upscaleModels = models.filter(
        (model) => model.type === "image-upscale"
      );
      const selectedUpscaleModel =
        upscaleModels.length > 0
          ? findModelByCatalogueId(upscaleModels, selectedUpscaleModelId) ||
            upscaleModels[0]
          : null;

      if (!selectedImageGenerationModel) {
        console.warn("No valid model found, defaulting to first model.");
        removeSessionItem("a2i-image-generation-model-id");
      }

      if (!selectedVideoGenearationModel) {
        console.warn(
          "No valid video generation model found, defaulting to first video model."
        );
        removeSessionItem("a2i-video-generation-model-id");
      }

      if (!selectedRemixModel) {
        console.warn(
          "No valid remix model found, defaulting to first remix model."
        );
        removeSessionItem("a2i-remix-model-id");
      }

      if (!selectedVtonModel) {
        console.warn(
          "No valid VTON model found, defaulting to first VTON model."
        );
        removeSessionItem("a2i-vton-model-id");
      }

      if (!selectedUpscaleModel) {
        console.warn(
          "No valid Upscale model found, defaulting to first Upscale model."
        );
        removeSessionItem("a2i-upscale-model-id");
      }

      if (selectedImageGenerationModel) {
        setSessionItem(
          "a2i-image-generation-model-id",
          selectedImageGenerationModel.id,
        );
      }
      if (selectedVideoGenearationModel) {
        setSessionItem(
          "a2i-video-generation-model-id",
          selectedVideoGenearationModel.id,
        );
      }
      if (selectedRemixModel) {
        setSessionItem("a2i-remix-model-id", selectedRemixModel.id);
      }
      if (selectedVtonModel) {
        setSessionItem("a2i-vton-model-id", selectedVtonModel.id);
      }
      if (selectedUpscaleModel) {
        setSessionItem("a2i-upscale-model-id", selectedUpscaleModel.id);
      }

      set({
        models: models,
        selectedImageGenerationModel: selectedImageGenerationModel,
        selectedVideoGenearationModel: selectedVideoGenearationModel,
        selectedRemixModel: selectedRemixModel,
        selectedVtonModel: selectedVtonModel,
        selectedUpscaleModel: selectedUpscaleModel,
      });
    },

    isModelsFetched: false,
    setIsModelsFetched: (isFetched) => set({ isModelsFetched: isFetched }),

    selectedImageGenerationModel:
      getSessionItem("a2i-image-generation-model-id") || null,
    setSelectedImageGenerationModelByModelId: (modelId) => {
      const models = get().models;
      const model = findModelByProviderId(models, modelId, "image");
      if (model) {
        // Save to session storage
        setSessionItem("a2i-image-generation-model-id", model.id);

        set({ selectedImageGenerationModel: model });
      }
    },
    setSelectedImageGenerationModel: (model) => {
      // Save to session storage
      setSessionItem("a2i-image-generation-model-id", model.id);

      set({ selectedImageGenerationModel: model });
    },

    selectedVideoGenearationModel:
      getSessionItem("a2i-video-generation-model-id") || null,
    setSelectedVideoGenearationModelByModelId: (modelId) => {
      const models = get().models;
      const model = findModelByProviderId(models, modelId, "video");
      if (model) {
        // Save to session storage
        setSessionItem("a2i-video-generation-model-id", model.id);
        set({ selectedVideoGenearationModel: model });
      }
    },
    setSelectedVideoGenearationModel: (model) => {
      // Save to session storage
      setSessionItem("a2i-video-generation-model-id", model.id);

      set({ selectedVideoGenearationModel: model });
    },

    selectedRemixModel: getSessionItem("a2i-remix-model-id") || null,
    setSelectedRemixModelByModelId: (modelId) => {
      const models = get().models;
      const model = findModelByProviderId(models, modelId, "remix");
      if (model) {
        // Save to session storage
        setSessionItem("a2i-remix-model-id", model.id);

        set({ selectedRemixModel: model });
      }
    },
    setSelectedRemixModel: (model) => {
      // Save to session storage
      setSessionItem("a2i-remix-model-id", model.id);

      set({ selectedRemixModel: model });
    },

    selectedVtonModel: getSessionItem("a2i-vton-model-id") || null,
    setSelectedVtonModel: (model) => {
      // Save to session storage
      setSessionItem("a2i-vton-model-id", model.id);

      set({ selectedVtonModel: model });
    },

    selectedUpscaleModel: getSessionItem("a2i-upscale-model-id") || null,
    setSelectedUpscaleModel: (model) => {
      // Save to session storage
      setSessionItem("a2i-upscale-model-id", model.id);

      set({ selectedUpscaleModel: model });
    },
  };
});
